import type { WebSocketEventEnum } from '@gap/backend/websockets/models/WebSocketEventEnum'
import type { WebSocketResponseDto } from '@gap/backend/websockets/models/WebSocketResponseDto'

/**
 * Tracks the in-flight WebSocket subscriptions so we can replay them after a
 * reconnect (new connectionId on the API Gateway side means the server-held
 * subscription rows are stale, even though our in-process callbacks still are).
 *
 * Storage is intentionally split: full subscription records (replayable wire
 * payloads) live alongside the callbacks. On reconnect, getReplayBatches()
 * returns wire-ready batches grouped by (orgId, userId).
 */

export type SubscriptionCallback = (response: WebSocketResponseDto) => Promise<void> | undefined | void

export interface RegistrySubscription {
   event: WebSocketEventEnum
   entityId: string
   message?: string
   callback?: SubscriptionCallback
}

export interface ReplayBatch {
   orgId: string
   userId: string
   subscriptions: { event: WebSocketEventEnum; entityId: string; message?: string }[]
}

interface StoredSubscription {
   orgId: string
   userId: string
   event: WebSocketEventEnum
   entityId: string
   message?: string
}

/** The identity a subscription is held under: its event and the entity it names. */
export function subscriptionKey(params: { event: WebSocketEventEnum; entityId: string }) {
   return `${params.event}:${params.entityId}` as const
}

/** The same identity for a wire subscription, whose entityId is optional. */
export function toSubscriptionKey(subscription: { event: WebSocketEventEnum; entityId?: string }) {
   return subscriptionKey({ event: subscription.event, entityId: subscription.entityId ?? '' })
}

export default class SubscriptionRegistry {
   private readonly stored = new Map<string, StoredSubscription>()
   private readonly callbacks = new Map<string, SubscriptionCallback>()

   add = (orgId: string, userId: string, subscription: RegistrySubscription) => {
      const key = subscriptionKey(subscription)
      this.stored.set(key, {
         orgId,
         userId,
         event: subscription.event,
         entityId: subscription.entityId,
         message: subscription.message,
      })
      if (subscription.callback) this.callbacks.set(key, subscription.callback)
   }

   remove = (subscription: Pick<RegistrySubscription, 'event' | 'entityId'>) => {
      const key = subscriptionKey(subscription)
      this.stored.delete(key)
      this.callbacks.delete(key)
   }

   getCallback = (event: WebSocketEventEnum, entityId: string): SubscriptionCallback | undefined => {
      return this.callbacks.get(subscriptionKey({ event, entityId }))
   }

   /**
    * Returns wire-ready batches grouped by (orgId, userId). Empty array when
    * nothing is registered — caller should treat that as "nothing to replay".
    */
   getReplayBatches = (): ReplayBatch[] => {
      const batches = new Map<string, ReplayBatch>()
      for (const sub of this.stored.values()) {
         const batchKey = `${sub.orgId}::${sub.userId}`
         let batch = batches.get(batchKey)
         if (!batch) {
            batch = { orgId: sub.orgId, userId: sub.userId, subscriptions: [] }
            batches.set(batchKey, batch)
         }
         batch.subscriptions.push({ event: sub.event, entityId: sub.entityId, message: sub.message })
      }
      return [...batches.values()]
   }

   size = (): number => this.stored.size

   clear = () => {
      this.stored.clear()
      this.callbacks.clear()
   }
}
